/**
 * Recovery from a stale deploy. Every deploy renames the built files, so a tab opened before it
 * still asks for chunks that no longer exist ("Failed to fetch dynamically imported module").
 * A reload picks up the new build — so on that error the page reloads itself, ONCE.
 *
 * The guard is a timestamp in sessionStorage (per tab, survives the reload): a second failure
 * within the window means the reload did not help, so the error is left to show rather than
 * looping. Without sessionStorage there is no way to know a reload already happened, so nothing
 * reloads at all.
 */

export const RELOAD_KEY = 'sp_stale_chunk_reload_at';
export const RELOAD_WINDOW_MS = 5 * 60 * 1000;

// Chrome / Firefox / Safari wordings of the same event, plus webpack-style chunk names.
const CHUNK_ERROR = /Loading chunk .* failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i;

export function isStaleChunkError(error: unknown): boolean {
    const message = typeof error === 'string' ? error : (error as any)?.message;

    return typeof message === 'string' && CHUNK_ERROR.test(message);
}

type Env = {
    storage: Pick<Storage, 'getItem' | 'setItem'> | null;
    now:     () => number;
    reload:  () => void;
};

function browserEnv(): Env {
    let storage: Env['storage'] = null;
    try {
        storage = window.sessionStorage;
    } catch {
        // blocked storage (private mode, embedded views): no guard, so no reload
    }

    return {storage, now: () => Date.now(), reload: () => window.location.reload()};
}

/** Reloads the page unless it already did so within the window. Returns whether it reloaded. */
export function reloadOnceForStaleChunk(env: Env = browserEnv()): boolean {
    if (!env.storage) {
        return false;
    }
    try {
        const last = Number(env.storage.getItem(RELOAD_KEY) || 0);
        if (last && env.now() - last < RELOAD_WINDOW_MS) {
            return false;
        }
        env.storage.setItem(RELOAD_KEY, String(env.now()));
    } catch {
        return false;
    }
    env.reload();

    return true;
}

/** Vite fires vite:preloadError when a dynamic import's chunk cannot be fetched. */
export function installStaleChunkReload(): void {
    window.addEventListener('vite:preloadError', (event) => {
        if (reloadOnceForStaleChunk()) {
            event.preventDefault(); // the page is reloading; don't also throw into the app
        }
    });
}
